import type { SerializedFile } from "@repo/models";

import { GridFiltersController } from "@repo/baseCloud";
import { downloadFile } from "@repo/helpers";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	ButtonTypes,
	DropDownList,
	FileUploader,
} from "@repo/ui";
import { useCallback, useState } from "react";

import { BRANCHES, MARKETERS, STATES, useFilterItemQuery } from "../../../api/queries";
import RepsApi from "../../../api/repsApi";

interface Props {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	showFilters: boolean;
}

export function BulkImportModal({ visible, onClose, onSuccess, showFilters }: Props) {
	const [selectedMarketer, setSelectedMarketer] = useState<number | undefined>(undefined);
	const [selectedState, setSelectedState] = useState<number | undefined>(undefined);
	const [selectedOffice, setSelectedOffice] = useState<number | undefined>(undefined);

	const [marketers] = useFilterItemQuery([MARKETERS], () => GridFiltersController.getMarketers(), {
		enabled: visible && showFilters,
	});

	const [states] = useFilterItemQuery([STATES, selectedMarketer?.toString()], () =>
		GridFiltersController.getStates({ marketerId: selectedMarketer }),
	);

	const [offices] = useFilterItemQuery([BRANCHES, selectedMarketer?.toString(), selectedState?.toString()], () =>
		GridFiltersController.getBranches({ marketerId: selectedMarketer, stateId: selectedState }),
	);

	const [files, setFiles] = useState<SerializedFile[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);

	const handleMarketerChange = (val: number | undefined) => {
		setSelectedMarketer(val);
		setSelectedState(undefined);
		setSelectedOffice(undefined);
	};

	const handleStateChange = (val: number | undefined) => {
		setSelectedState(val);
		setSelectedOffice(undefined);
	};

	const handleDownloadTemplate = useCallback(async () => {
		try {
			const blob = await RepsApi.downloadBulkUploadTemplate();
			const url = window.URL.createObjectURL(blob);
			downloadFile(url, "bulk-upload-template.xlsx");
		} catch (e) {
			console.error("Failed to download template", e);
		}
	}, []);

	const handleSubmit = useCallback(async () => {
		if (files.length === 0) return;

		setIsSubmitting(true);
		setErrors([]);

		try {
			const formData = new FormData();
			// We need to convert SerializedFile back to Blob/File if possible,
			// or if AgentPortalApi.processBulkImportFile expects the base64 data.
			// Usually, for Multipart, we want real Files.
			// Since FileUploader gives us SerializedFile (with url as dataURL),
			// we might need to convert it back.

			const file = files[0];
			if (file.url) {
				const res = await fetch(file.url);
				const blob = await res.blob();
				formData.append("bulkUploadFile", blob, file.name);
			}

			if (showFilters) {
				formData.append("bulkImportMarketers", selectedMarketer?.toString() ?? "");
				formData.append("bulkImportStates", selectedState?.toString() ?? "");
				formData.append("bulkImportBranchId", selectedOffice?.toString() ?? "");
			}

			const result = await RepsApi.processBulkImportFile(formData);
			if (result && result.length > 0) {
				setErrors(result);
			} else {
				onSuccess();
				onClose();
			}
		} catch (e: unknown) {
			setErrors(["An unexpected error occurred during import."]);
			console.error(e);
		} finally {
			setIsSubmitting(false);
		}
	}, [files, selectedMarketer, selectedState, selectedOffice, showFilters, onSuccess, onClose]);

	const renderBody = () => (
		<div className="flex flex-col gap-4 p-4">
			{showFilters && (
				<>
					<DropDownList
						label="Vendor"
						values={marketers.data ?? []}
						valueProp="id"
						textProp="value"
						value={selectedMarketer}
						changeHandler={handleMarketerChange}
						placeholder="Select Vendor"
						fullLength
					/>
					<DropDownList
						label="State"
						values={states.data ?? []}
						valueProp="id"
						textProp="value"
						value={selectedState}
						changeHandler={handleStateChange}
						placeholder="Select State"
						disabled={!selectedMarketer}
						fullLength
					/>
					<DropDownList
						label="Office"
						values={offices.data ?? []}
						valueProp="id"
						textProp="name"
						value={selectedOffice}
						changeHandler={(val) => setSelectedOffice(val)}
						placeholder="Select Office"
						disabled={!selectedState}
						fullLength
					/>
				</>
			)}

			<div className="text-center">
				<FileUploader
					id="bulkUploadFile"
					label="Select Bulk Import File"
					onFilesAdded={(_, newFiles) => setFiles(newFiles)}
					files={files}
					acceptFileTypes={[".xlsx"]}
					multiple={false}
					autoReplace={true}
				/>
			</div>

			<div className="text-center">
				<Button onClick={handleDownloadTemplate} types={[ButtonTypes.Primary]}>
					Download the import template
				</Button>
			</div>

			{errors.length > 0 && (
				<textarea
					readOnly
					value={errors.join("\n")}
					style={{ width: "100%", height: "170px", color: "red", border: "1px solid red", padding: "8px" }}
				/>
			)}
		</div>
	);

	return (
		<AlertDialog open={visible} onOpenChange={(open) => !open && onClose()}>
			<AlertDialogContent style={{ maxWidth: "500px" }}>
				<AlertDialogHeader>
					<AlertDialogTitle>Bulk Import Representatives</AlertDialogTitle>
				</AlertDialogHeader>

				<AlertDialogDescription asChild>{renderBody()}</AlertDialogDescription>

				<AlertDialogFooter>
					<AlertDialogAction
						onClick={handleSubmit}
						types={[ButtonTypes.Primary]}
						disabled={isSubmitting || files.length === 0 || (showFilters && !selectedOffice)}
					>
						{isSubmitting ? "Submitting..." : "Submit"}
					</AlertDialogAction>
					<AlertDialogCancel onClick={onClose} types={[ButtonTypes.Outline]}>
						Cancel
					</AlertDialogCancel>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
